// author-liumingyang 2022-3-8  经分
import React, { useMemo } from 'react';
import EChartCommon from '@/pages/opinion/components/EChartCommon';
import {
  formatChartParamsDate,
  imgShowElement,
  textShowElement,
} from '@/pages/opinion/components/ChartCommon/formatter';
import { baseColors, extendColors } from '@/pages/opinion/constants';
import { useIntl, getLocale } from 'umi';
import { dealwithTitle } from '../../utils';
import { DBO_EC, DBO_V, DBO_Y } from '@op/utils/format';
import {
  chartIconRich,
  getLegendFormat,
} from '@op/pages/Overview/KeyMetrics/MentionsSentiment/MetricsChart/Charts/utils';
import positiveIcon from '@op/assets/svg/steam_positive.svg';
import negativeIcon from '@op/assets/svg/steam_negative.svg';

type SeriesDataType = {
  value: number;
  originName: string;
  originValue: number;
  percentage: string; 
};

const colorArr = [...baseColors, ...extendColors];

export default function BarEchart({
  data,
  height = 300,
}: {
  data: any;
  height?: number;
}) {
  const { formatMessage } = useIntl();
  const locale = getLocale();

  // 设置图形配置
  const makeupOptions = () => {
    const negativeList: Array<SeriesDataType> = [];
    const positiveList: Array<SeriesDataType> = [];
    const total: Array<number> = [];
    const yAxisList: Array<string> = [];

    function getPercentage(value: number, total: number, decimal = 2) {
      return value === 0 ? '' : DBO_V.percentage(value, total, decimal);
    }
 
    data.forEach(
      (item: {
        negative: number;
        positive: number;
        count: number;
        key: string;
      }) => {
        const { positive, negative, count, key } = item;
        negativeList.push({
          value: negative * -1,
          originName: key,
          originValue: negative,
          percentage: getPercentage(negative, count), 
        });
        positiveList.push({
          value: positive,
          originName: key,
          originValue: positive,
          percentage: getPercentage(positive, count), 
        });
        total.push(count);
        yAxisList.push(key);
      },
    );

    const rightMax = Math.max(
      ...positiveList.map((item) => item.originValue),
      ...negativeList.map((item) => item.originValue),
    );
    const maxValue = DBO_EC.ceil(rightMax * 1.2);
 
    const opts = {
      grid: { left: 12, right: 0 },
      color: colorArr,
      tooltip: {
        formatter: (params: any) => {
          const title = dealwithTitle(params[0]?.axisValue);
          return formatChartParamsDate({
            title,
            params,
            textShowFun: (marker: string, seriesName: string, data) => {
              return textShowElement(
                marker,
                imgShowElement(
                  data.seriesName === 'Positive' ? positiveIcon : negativeIcon,
                  seriesName,
                ),
              );
            },
            listTitleFunc: (item) => formatMessage({ id: item.seriesName }),
            valueFormat: (value, element) => {
              return `${DBO_V.reviews(Math.abs(value))} (${
                element.data.percentage || "0%"
              })`;
            },
            dealwithRecords: (records) => {
              const total = records.reduce((acc, cur) => {
                return acc + Math.abs(cur?.originValue);
              }, 0);
              records.sort((a, b) => {
                return a.originText === 'Positive' ? 1 : -1;
              });
              return [
                {
                  text: formatMessage({ id: 'Mentions' }),
                  originText: 'Mentions',
                  value: `${DBO_V.reviews(total)}`,
                  originValue: total,
                },
                ...records,
              ];
            },
          });
        },
      },
      legend: {
        data: [
          {
            name: 'Negative',
            itemStyle: { color: '#F17C86' },
          },
          {
            name: 'Positive',
            itemStyle: { color: '#69D19C' },
          },
        ],
        textStyle: { rich: chartIconRich },
        formatter: (params: string) => {
          const func = getLegendFormat(formatMessage, { byType: 'by_rating' });
          return func(params);
        },
      },
      yAxis: [
        {
          position: 'left',
          axisLabel: {
            show: true,
            margin: 15,
            interval: 0,
            align: 'right',
            formatter: (value: string) => {
              return dealwithTitle(value);
            },
          },
          splitLine: {
            show: false,
            lineStyle: { color: '#E5E5E5', type: 'dashed' },
          },
          axisLine: {
            show: false,
          },
          type: 'category',
          axisTick: {
            show: false,
          },
          gridIndex: 0,
          data: yAxisList,
        },
        {
          type: 'category',
          position: 'right',
          data: total,
          axisLabel: {
            formatter: (value: number) => `{value|${DBO_Y.reviews(value)}}`,
            show: true,
            width: 40,
            rich: {
              value: {
                align: 'right',
              },
            },
            fontWeight: 400,
            fontSize: 12,
            lineHeight: 16,
            padding: [0, 0, 0, 0],
          },
          splitLine: {
            show: false,
            lineStyle: { color: '#E5E5E5', type: 'dashed' },
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
        },
      ],
      xAxis: {
        axisTick: {
          alignWithLabel: true,
          lineStyle: {
            color: '#000000',
            opacity: '0.25',
          },
        },
        boundaryGap: true,
        type: 'value',
        splitLine: {
          show: true,
          lineStyle: { color: '#E5E5E5', type: 'dashed' },
        },
        axisLine: {
          show: true,
          lineStyle: { color: '#E5E5E5' },
        },
        axisLabel: {
          interval: 'auto',
          formatter: (value: number) => {
            return DBO_Y.reviews(Math.abs(value));
          },
        },
        max: maxValue,
        min: -maxValue,
      },
      series: [
        {
          name: 'Negative',
          type: 'bar',
          stack: '1',
          color: '#F17C86',
          barMaxWidth: 30,
          emphasis: {
            disable: true,
          },
          data: negativeList,
        },
        {
          name: 'Positive',
          type: 'bar',
          stack: '1',
          color: '#69D19C',
          barMaxWidth: 30,
          data: positiveList,
          label: {
            show: true,
            position: 'right',
            formatter: (params: {
              value: number;
              data: { percentage: string };
              dataIndex: number;
            }) => {
              return params.data.percentage;
            },
          },
        },
      ],
    };
    return opts;
  };

  const options = useMemo(() => {
    if (data.length === 0) {
      return {};
    }
    return makeupOptions();
  }, [data, locale]);

  return (
    <EChartCommon
      optionsProps={{ type: 'bar' }}
      options={options}
      rightTitle={formatMessage({ id: 'Mentions' })}
      empty={data.length === 0}
      height={height}
    ></EChartCommon>
  );
}
